import { buildHistorialFieldMap, type HistorialFullEntry } from '@/hooks/useLatestHistorialMap';
import { historialKey } from '@/utils/revisionHistorial';
import type {
  DiffActivaEvidenciaValue,
  DiffActivaTernaCambios,
  EvaluacionAdminResponse,
  EvaluacionTernaAdmin,
  TernaAdmin,
} from '@/types/evaluacionAdmin';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ExternalLink,
  GitCompare,
  XCircle,
} from 'lucide-react';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { TernaFieldRevision, isTernaFieldPendiente } from './TernaFieldRevision';
import { ShiftActionOverlay } from './ShiftActionOverlay';
import { RevisionFieldModal } from './RevisionFieldModal';

type CalificacionType = 'C' | 'NC' | 'CP' | 'NA';

interface ReviewModalState {
  kind: 'terna' | 'requisito';
  titulo: string;
  textoActual?: string | null;
  textoAnterior?: string | null;
  numeral?: string;
  capitulo?: string;
  calActual?: CalificacionType | null;
  calAnterior?: CalificacionType | null;
  historialList?: HistorialFullEntry[];
  estadoActual?: 'APROBADO' | 'RECHAZADO' | 'COMENTADO';
  observacionActual?: string;
  onAprobar?: (observacion?: string) => Promise<void>;
  onRechazar?: (observacion?: string) => Promise<void>;
}

function ultimoVeredictoAbierto(
  entries: HistorialFullEntry[] | undefined
): { estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string } | undefined {
  if (!entries || entries.length === 0) return undefined;
  const ordenados = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimo = ordenados[ordenados.length - 1];
  if (ultimo.estado === 'APROBADO' || ultimo.estado === 'RECHAZADO' || ultimo.estado === 'COMENTADO') {
    return { estado: ultimo.estado, observacion: ultimo.observacion ?? undefined };
  }
  return undefined;
}

/** Agrupa filas consecutivas de `visibleRows` que comparten el mismo requisito en una columna,
 * para que la mini-matriz del paso a paso las combine en una sola celda con rowSpan — igual que
 * ya hace MatrizInline. Sin esto, un requisito compartido por varias ternas salia repetido en
 * filas separadas en vez de una sola fila abarcando todas. */
function calcularSpansStepper(rows: TernaAdmin[], columnas: { ntcId: number; orden: number }[]) {
  const spans: Record<number, { startRow: number; span: number; reqId: string | null }[]> = {};
  for (const col of columnas) {
    spans[col.orden] = [];
    let i = 0;
    while (i < rows.length) {
      const reqId = col.orden === 1 ? rows[i].requisitoC1Id : col.orden === 2 ? rows[i].requisitoC2Id : rows[i].requisitoC3Id;
      let j = i + 1;
      while (
        j < rows.length &&
        reqId !== null &&
        (col.orden === 1 ? rows[j].requisitoC1Id : col.orden === 2 ? rows[j].requisitoC2Id : rows[j].requisitoC3Id) === reqId
      ) {
        j++;
      }
      spans[col.orden]!.push({ startRow: i, span: j - i, reqId });
      i = j;
    }
  }
  return spans;
}

const CAL_STYLES: Record<string, string> = {
  C: 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]',
  NC: 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]',
  CP: 'bg-[#fffbeb] text-[#92400e] border-[#fde68a]',
  NA: 'bg-gray-100 text-gray-600 border-gray-300',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepItem {
  /** Casi siempre un solo elemento — más de uno cuando varias evaluacionesTernas consecutivas
   * (en el orden real de la Matriz) comparten C1, C2 o C3 y por eso se fusionan visualmente en
   * un solo paso, igual que hace MatrizInline con calcularSpansRO. */
  evalTernas: EvaluacionTernaAdmin[];
  evalKeys: string[];
  diffTernas: DiffActivaTernaCambios[];
}

/** Mismo orden canónico que `buildGruposDesdeAdmin` en MatrizInline.tsx: por `orden` ascendente,
 * y dentro de un mismo `orden`, las filas con `grupo` van primero (ordenadas por el propio string
 * de grupo) y luego las sueltas por ternaId. Necesario para decidir adyacencia real, no la del
 * array ya filtrado de pasos (que puede saltarse ternas sin cambios y volver "vecinos" a pasos
 * que en la Matriz no lo son). */
function ordenCanonicoTernas(ternas: TernaAdmin[]): TernaAdmin[] {
  const porOrden = new Map<number, TernaAdmin[]>();
  for (const t of ternas) {
    const key = t.orden || 0;
    if (!porOrden.has(key)) porOrden.set(key, []);
    porOrden.get(key)!.push(t);
  }
  return Array.from(porOrden.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([, filas]) =>
      filas.sort((a, b) => {
        if (a.grupo && !b.grupo) return -1;
        if (!a.grupo && b.grupo) return 1;
        if (a.grupo && b.grupo && a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
        return a.id - b.id;
      })
    );
}

/** ¿Comparten C1, C2 o C3 (no nulo)? Mismo criterio columna-por-columna que calcularSpansRO/
 * calcularSpansStepper — si cualquiera de las tres coincide, las filas se fusionan visualmente. */
function comparteAlgunaColumna(a: TernaAdmin, b: TernaAdmin): boolean {
  return (
    (a.requisitoC1Id != null && a.requisitoC1Id === b.requisitoC1Id) ||
    (a.requisitoC2Id != null && a.requisitoC2Id === b.requisitoC2Id) ||
    (a.requisitoC3Id != null && a.requisitoC3Id === b.requisitoC3Id)
  );
}

/** Id de grupo de fusión visual para cada terna: dos ternas quedan en el mismo grupo si son
 * (transitivamente) adyacentes en el orden canónico y comparten alguna columna — exactamente lo
 * que la Matriz combina en un solo span. */
function calcularGruposDeFusion(ternasOrdenadas: TernaAdmin[]): Map<number, number> {
  const grupoDeTerna = new Map<number, number>();
  let grupoActual = 0;
  for (let i = 0; i < ternasOrdenadas.length; i++) {
    const prev = ternasOrdenadas[i - 1];
    const actual = ternasOrdenadas[i];
    if (i === 0 || !prev || !comparteAlgunaColumna(prev, actual)) grupoActual++;
    grupoDeTerna.set(actual.id, grupoActual);
  }
  return grupoDeTerna;
}

export interface TernaRevisionStepperProps {
  asesoriaId: number;
  data: EvaluacionAdminResponse;
  diffTernaMap: Map<string, DiffActivaTernaCambios>;
  latestHistorialMap: Map<string, { estado: string; observacion?: string | null }>;
  onStepSaved: (savedEvalTernaIds: number[]) => void;
  initialIndex?: number;
  historialRevision?: any[];
  /** Guarda de inmediato (sin batch) — mismo contrato que MatrizInline. */
  onTernaFieldVeredicto?: (
    evalTernaId: number,
    campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
    estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO',
    observacion?: string
  ) => Promise<void>;
  onRequisitoVeredicto?: (evaluacionRequisitoId: number, estado: 'APROBADO' | 'RECHAZADO', observacion?: string) => Promise<void>;
}

export function isStepPendiente(
  diffTerna: DiffActivaTernaCambios,
  evalTernaId: number,
  latestHistorialMap: Map<string, { estado: string; observacion?: string | null }>
): boolean {
  const TERMINAL = new Set(['APROBADO', 'RECHAZADO']);
  if (diffTerna.observaciones !== undefined) {
    const h = latestHistorialMap.get(historialKey('OBSERVACIONES', evalTernaId));
    if (!h || !TERMINAL.has(h.estado)) return true;
  }
  if (diffTerna.planMejora !== undefined) {
    const h = latestHistorialMap.get(historialKey('PLANMEJORA', evalTernaId));
    if (!h || !TERMINAL.has(h.estado)) return true;
  }
  if (diffTerna.fechaImplementacion !== undefined) {
    const h = latestHistorialMap.get(historialKey('FECHAIMPLEMENTACION', evalTernaId));
    if (!h || !TERMINAL.has(h.estado)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// TernaRevisionStepper
// ---------------------------------------------------------------------------

export function TernaRevisionStepper({
  asesoriaId: _asesoriaId,
  data,
  diffTernaMap,
  latestHistorialMap,
  onStepSaved,
  initialIndex = 0,
  historialRevision,
  onTernaFieldVeredicto,
  onRequisitoVeredicto,
}: TernaRevisionStepperProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [expanded, setExpanded] = useState(false);
  const [expandedDescs, setExpandedDescs] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [expandAllTexts, setExpandAllTexts] = useState(false);
  const [wordDiffAll, setWordDiffAll] = useState(false);
  const [reviewModal, setReviewModal] = useState<ReviewModalState | null>(null);
  const [aprobandoFila, setAprobandoFila] = useState(false);

  const columnas = useMemo(
    () =>
      (data.combinacion?.ntcs ?? data.ntcs.map((n) => ({ ntcId: n.ntcId, nombre: n.nombre, orden: n.orden || 1 }))).sort(
        (a, b) => a.orden - b.orden
      ),
    [data.combinacion, data.ntcs]
  );

  const reqMap = useMemo(() => {
    const m = new Map<string, (typeof data.requisitos)[0]>();
    data.requisitos.forEach((r) => m.set(r.requisitoId, r));
    return m;
  }, [data.requisitos]);

  const calMap = useMemo(() => {
    const m = new Map<string, CalificacionType | null>();
    data.evaluacion.forEach((e) => m.set(e.requisitoId, e.calificacion));
    return m;
  }, [data.evaluacion]);

  const prevCalMap = useMemo(() => {
    const m = new Map<string, CalificacionType | null>();
    data.diffActiva?.requisitos.forEach((r) => {
      if (r.calificacionAnterior !== undefined) m.set(r.requisitoId, r.calificacionAnterior ?? null);
    });
    return m;
  }, [data.diffActiva]);

  // Maps requisitoId (UUID) → EvaluacionRequisito.id (integer) for historial lookups.
  // RevisionElementoHistorial stores the integer id as elementoId for REQUISITO entries.
  const evalReqIdMap = useMemo(() => {
    const m = new Map<string, number>();
    data.evaluacion.forEach((e) => {
      if (e.evaluacionRequisito?.id != null) m.set(e.requisitoId, e.evaluacionRequisito.id);
    });
    return m;
  }, [data.evaluacion]);

  const evalEstadoMap = useMemo(() => {
    const m = new Map<string, string | undefined>();
    data.evaluacion.forEach((e) => m.set(e.requisitoId, e.evaluacionRequisito?.estadoRevision));
    return m;
  }, [data.evaluacion]);

  const historialFieldMap = useMemo(() => buildHistorialFieldMap((historialRevision ?? []) as any), [historialRevision]);

  // ¿Alguna calificación (C1/C2/C3) de las ternas asociadas a esta clave (terna:id o grupo:X)
  // cambió respecto a la asesoría anterior? Antes, un cambio de calificación sin cambio de
  // campos de terna (observaciones/planMejora/fecha) era invisible para el paso a paso — el
  // requisito nunca llegaba a tener un paso propio donde revisarlo.
  const keyHasCalChange = useCallback(
    (evalKey: string): boolean => {
      const esGrupo = evalKey.startsWith('grupo:');
      const ternasDeLaClave = esGrupo
        ? data.ternas.filter((t) => t.grupo === evalKey.slice('grupo:'.length))
        : data.ternas.filter((t) => `terna:${t.id}` === evalKey);
      return ternasDeLaClave.some((t) =>
        [t.requisitoC1Id, t.requisitoC2Id, t.requisitoC3Id].some((reqId) => {
          if (!reqId) return false;
          const cal = calMap.get(reqId) ?? null;
          const prevCal = prevCalMap.get(reqId);
          return prevCal !== undefined && prevCal !== cal;
        })
      );
    },
    [data.ternas, calMap, prevCalMap]
  );

  // Fila representativa de un evalTerna (misma regla que `defaultRows` más abajo): la propia
  // ternaId, o la primera fila de su grupo.
  const filaRepresentativa = useCallback(
    (et: EvaluacionTernaAdmin): TernaAdmin | undefined =>
      et.grupo ? data.ternas.find((t) => t.grupo === et.grupo) : data.ternas.find((t) => t.id === et.ternaId),
    [data.ternas]
  );

  const steps: StepItem[] = useMemo(() => {
    // Deduplicar por clave (grupo:X / terna:Y) ANTES de armar candidatos — mismo criterio que
    // `evalTernaMap` en MatrizInline. El backend puede devolver más de un `EvaluacionTerna` para
    // la misma clave (ej. un registro "fantasma" con ternaId:null y campos vacíos junto al real);
    // sin dedup, ese fantasma generaba su propio "paso" con observaciones/planMejora vacías que
    // nunca aparecían en el historial real, contándose como pendiente aunque todo lo visible ya
    // estuviera Aprobado. Igual que el Map de la Matriz, se queda con el ÚLTIMO por clave.
    const evaluacionesTernasDedup = new Map<string, EvaluacionTernaAdmin>();
    for (const et of data.evaluacionesTernas) {
      const key = et.ternaId ? (et.grupo ? `grupo:${et.grupo}` : `terna:${et.ternaId}`) : et.grupo ? `grupo:${et.grupo}` : null;
      if (key) evaluacionesTernasDedup.set(key, et);
    }

    const candidatos = Array.from(evaluacionesTernasDedup.values())
      .map((et) => {
        const byId = et.ternaId ? `terna:${et.ternaId}` : null;
        const byGrupo = et.grupo ? `grupo:${et.grupo}` : null;
        const key = (byId && diffTernaMap.has(byId) ? byId : null) || (byGrupo && diffTernaMap.has(byGrupo) ? byGrupo : null);
        if (key) return { evalTerna: et, evalKey: key, diffTerna: diffTernaMap.get(key)! };

        // Sin diff de campos de terna: igual amerita un paso si cambió alguna calificación de
        // sus requisitos — antes esos cambios quedaban sin ningún paso donde revisarlos.
        const fallbackKey = byId ?? byGrupo;
        if (fallbackKey && keyHasCalChange(fallbackKey)) {
          return { evalTerna: et, evalKey: fallbackKey, diffTerna: {} };
        }
        return null;
      })
      .filter((s): s is { evalTerna: EvaluacionTernaAdmin; evalKey: string; diffTerna: DiffActivaTernaCambios } => s !== null);

    // Orden canónico real (el mismo que arma la Matriz) para decidir adyacencia de verdad — no
    // la del array ya filtrado de candidatos, que puede "acercar" pasos que en la Matriz no son
    // vecinos (porque hay ternas sin cambios entre medio que no generan paso propio).
    const ternasOrdenadas = ordenCanonicoTernas(data.ternas);
    const posDeTerna = new Map(ternasOrdenadas.map((t, i) => [t.id, i]));
    const grupoDeTerna = calcularGruposDeFusion(ternasOrdenadas);

    const conPosicion = candidatos
      .map((c) => {
        const fila = filaRepresentativa(c.evalTerna);
        return fila ? { ...c, pos: posDeTerna.get(fila.id) ?? 0, fusionId: grupoDeTerna.get(fila.id) ?? -1 } : null;
      })
      .filter(
        (c): c is { evalTerna: EvaluacionTernaAdmin; evalKey: string; diffTerna: DiffActivaTernaCambios; pos: number; fusionId: number } =>
          c !== null
      )
      .sort((a, b) => a.pos - b.pos);

    const merged: StepItem[] = [];
    let lastFusionId: number | null = null;
    for (const c of conPosicion) {
      const last = merged[merged.length - 1];
      if (last && lastFusionId === c.fusionId) {
        last.evalTernas.push(c.evalTerna);
        last.evalKeys.push(c.evalKey);
        last.diffTernas.push(c.diffTerna);
      } else {
        merged.push({ evalTernas: [c.evalTerna], evalKeys: [c.evalKey], diffTernas: [c.diffTerna] });
      }
      lastFusionId = c.fusionId;
    }
    return merged;
  }, [data.evaluacionesTernas, data.ternas, diffTernaMap, keyHasCalChange, filaRepresentativa]);

  const TERNA_CAMPOS: { campo: 'observaciones' | 'planMejora' | 'fechaImplementacion'; tipoHist: string }[] = [
    { campo: 'observaciones', tipoHist: 'OBSERVACIONES' },
    { campo: 'planMejora', tipoHist: 'PLANMEJORA' },
    { campo: 'fechaImplementacion', tipoHist: 'FECHAIMPLEMENTACION' },
  ];

  type PendingItems = {
    requisitos: number[];
    camposTerna: { evalTernaId: number; campo: 'observaciones' | 'planMejora' | 'fechaImplementacion' }[];
  };

  // Items pendientes de veredicto por paso — mismo criterio que MatrizInline: un campo de terna
  // cuenta si diffTerna lo marca cambiado y su historial sigue sin resolver; un requisito cuenta
  // si su calificacion cambio y su estadoRevision sigue sin resolver. Alimenta el boton unico
  // "Aprobar fila" de cada paso y el indicador de progreso (punto verde = paso resuelto).
  const pendingByStep = useMemo<PendingItems[]>(() => {
    return steps.map((s) => {
      const pending: PendingItems = { requisitos: [], camposTerna: [] };
      const vistos = new Set<number>();

      s.evalTernas.forEach((evalTerna, idx) => {
        const diffTerna = s.diffTernas[idx]!;
        for (const { campo, tipoHist } of TERNA_CAMPOS) {
          if (diffTerna[campo] === undefined) continue;
          const h = latestHistorialMap.get(historialKey(tipoHist, evalTerna.id));
          if (isTernaFieldPendiente(h?.estado)) {
            pending.camposTerna.push({ evalTernaId: evalTerna.id, campo });
          }
        }

        const rowsDelPaso = evalTerna.grupo
          ? data.ternas.filter((t) => t.grupo === evalTerna.grupo)
          : evalTerna.ternaId
            ? data.ternas.filter((t) => t.id === evalTerna.ternaId)
            : [];
        for (const t of rowsDelPaso) {
          for (const reqId of [t.requisitoC1Id, t.requisitoC2Id, t.requisitoC3Id]) {
            if (!reqId) continue;
            const cal = calMap.get(reqId) ?? null;
            const prevCal = prevCalMap.get(reqId);
            if (prevCal === undefined || prevCal === cal) continue;
            const estadoRevision = evalEstadoMap.get(reqId);
            const estaPendiente = !estadoRevision || estadoRevision === 'PENDIENTE';
            const evalId = evalReqIdMap.get(reqId);
            if (estaPendiente && evalId && !vistos.has(evalId)) {
              vistos.add(evalId);
              pending.requisitos.push(evalId);
            }
          }
        }
      });

      return pending;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, latestHistorialMap, data.ternas, calMap, prevCalMap, evalEstadoMap, evalReqIdMap]);

  const openTernaReview = (
    evalTernaId: number,
    campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
    tipoHist: string,
    label: string,
    textoActual?: string | null,
    textoAnterior?: string | null
  ) => {
    const historialElemento = historialFieldMap.get(historialKey(tipoHist, evalTernaId));
    const current = ultimoVeredictoAbierto(historialElemento);
    setReviewModal({
      kind: 'terna',
      titulo: label,
      textoActual,
      textoAnterior,
      historialList: historialElemento,
      estadoActual: current?.estado,
      observacionActual: current?.observacion,
      onAprobar: async (observacion) => {
        await onTernaFieldVeredicto?.(evalTernaId, campo, 'APROBADO', observacion);
        onStepSaved([evalTernaId]);
      },
      onRechazar: async (observacion) => {
        await onTernaFieldVeredicto?.(evalTernaId, campo, 'RECHAZADO', observacion);
        onStepSaved([evalTernaId]);
      },
    });
  };

  const openRequisitoReview = (
    evalId: number,
    numeral: string,
    capitulo: string,
    descripcion: string,
    cal: CalificacionType | null,
    prev: CalificacionType | null
  ) => {
    const historialElemento = historialFieldMap.get(historialKey('REQUISITO', evalId));
    const current = ultimoVeredictoAbierto(historialElemento);
    setReviewModal({
      kind: 'requisito',
      titulo: `Requisito ${numeral}`,
      textoActual: descripcion,
      textoAnterior: null,
      numeral,
      capitulo,
      calActual: cal,
      calAnterior: prev,
      historialList: historialElemento,
      estadoActual: current?.estado,
      observacionActual: current?.observacion,
      onAprobar: async (observacion) => {
        await onRequisitoVeredicto?.(evalId, 'APROBADO', observacion);
        onStepSaved([evalId]);
      },
      onRechazar: async (observacion) => {
        await onRequisitoVeredicto?.(evalId, 'RECHAZADO', observacion);
        onStepSaved([evalId]);
      },
    });
  };

  const handleAprobarFila = async () => {
    const items = pendingByStep[currentIndex];
    if (!items) return;
    setAprobandoFila(true);
    try {
      await Promise.all([
        ...items.requisitos.map((evalId) => onRequisitoVeredicto?.(evalId, 'APROBADO')),
        ...items.camposTerna.map(({ evalTernaId, campo }) => onTernaFieldVeredicto?.(evalTernaId, campo, 'APROBADO')),
      ]);
      onStepSaved(items.camposTerna.map((c) => c.evalTernaId));
      if (currentIndex < steps.length - 1) {
        setCurrentIndex((i) => i + 1);
        setExpanded(false);
      }
    } finally {
      setAprobandoFila(false);
    }
  };

  if (steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <CheckCircle2 className="w-10 h-10 mb-3 text-green-400" />
        <p className="text-sm font-medium">No hay cambios pendientes de revisión</p>
      </div>
    );
  }

  const pasosPendientes = pendingByStep
    .map((pend, i) => ({ i, total: pend.requisitos.length + pend.camposTerna.length }))
    .filter((p) => p.total > 0);

  // `initialIndex` lo calcula el padre sobre un conteo de pasos SIN fusionar (una evaluacionTerna
  // = un paso); aquí varios pasos consecutivos pueden combinarse en uno (ver `steps` arriba), así
  // que el índice puede quedar fuera de rango. Cuando eso pasa, en vez de solo recortar al último
  // paso válido (dejando a el admin siempre en el paso equivocado), se prefiere saltar directo al
  // primer paso realmente pendiente — si no hay ninguno, recién ahí se recorta al último válido.
  const indiceFueraDeRango = currentIndex < 0 || currentIndex >= steps.length;
  const safeIndex = indiceFueraDeRango ? (pasosPendientes[0]?.i ?? Math.max(0, Math.min(currentIndex, steps.length - 1))) : currentIndex;
  if (safeIndex !== currentIndex) setCurrentIndex(safeIndex);
  const step = steps[safeIndex];
  if (!step) return null;
  const pendingActual = pendingByStep[safeIndex] ?? { requisitos: [], camposTerna: [] };
  const totalPendienteActual = pendingActual.requisitos.length + pendingActual.camposTerna.length;
  const isSaved = totalPendienteActual === 0;
  // Unión de las filas de TODAS las evaluacionesTernas fusionadas en este paso (normalmente una
  // sola, salvo cuando varios pasos consecutivos comparten C1/C2/C3 y se combinaron en uno).
  const defaultRows: TernaAdmin[] = step.evalTernas.flatMap((et) =>
    et.grupo ? data.ternas.filter((t) => t.grupo === et.grupo) : et.ternaId ? data.ternas.filter((t) => t.id === et.ternaId) : []
  );
  const ordenesDelPaso = new Set(defaultRows.map((r) => r.orden));
  const allRowsForOrden = defaultRows.length > 0 ? data.ternas.filter((t) => ordenesDelPaso.has(t.orden)) : defaultRows;
  const visibleRows = expanded ? allRowsForOrden : defaultRows;
  const canExpand = allRowsForOrden.length > defaultRows.length;

  const irAPaso = (i: number) => {
    setCurrentIndex(Math.max(0, Math.min(steps.length - 1, i)));
    setExpanded(false);
    setExpandAllTexts(false);
  };

  const irAlSiguientePendiente = () => {
    if (pasosPendientes.length === 0) return;
    const siguiente = pasosPendientes.find((p) => p.i > safeIndex) ?? pasosPendientes[0];
    irAPaso(siguiente!.i);
  };

  return (
    <div className="flex flex-col gap-0">
      {/* Progress header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={() => irAPaso(safeIndex - 1)}
          disabled={safeIndex === 0}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </button>
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-bold text-gray-700">
            {safeIndex + 1} / {steps.length}
          </span>
          <div className="flex gap-1 mt-0.5">
            {steps.map((_, i) => {
              const pend = pendingByStep[i];
              const totalPend = pend ? pend.requisitos.length + pend.camposTerna.length : 0;
              const resuelto = totalPend === 0;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => irAPaso(i)}
                  title={`Paso ${i + 1}${resuelto ? ' — resuelto' : ` — ${totalPend} sin resolver`}`}
                  className={`block h-1 rounded-full transition-all cursor-pointer ${
                    i === safeIndex ? 'w-4 bg-gray-700' : resuelto ? 'w-1.5 bg-green-400' : 'w-1.5 bg-amber-400'
                  }`}
                />
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => irAPaso(safeIndex + 1)}
          disabled={safeIndex === steps.length - 1}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 transition-colors"
        >
          Siguiente
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {(pasosPendientes.length > 0 || totalPendienteActual > 0) && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/30">
          {pasosPendientes.length > 0 ? (
            <div className="flex items-center gap-2">
              <select
                aria-label="Saltar a un paso pendiente"
                value=""
                onChange={(e) => {
                  if (e.target.value !== '') irAPaso(Number(e.target.value));
                }}
                className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
              >
                <option value="">{pasosPendientes.length} pendiente(s)</option>
                {pasosPendientes.map((p) => (
                  <option key={p.i} value={p.i}>
                    Paso {p.i + 1} — {p.total} sin resolver
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={irAlSiguientePendiente}
                className="text-[10px] font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2"
              >
                Ir al siguiente pendiente
              </button>
            </div>
          ) : (
            <span />
          )}
          {totalPendienteActual > 0 && (
            <button
              type="button"
              disabled={aprobandoFila}
              onClick={handleAprobarFila}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] hover:bg-[#dcf0e3] transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {aprobandoFila ? 'Aprobando...' : `Aprobar fila (${totalPendienteActual})`}
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div ref={scrollContainerRef} className="overflow-auto max-h-[65vh]">
        {/* ── Section 1: NTC mini-matrix ── */}
        <div className="border-b border-gray-100">
          {/* Header row */}
          <div className="flex bg-gray-50/80 text-[9px] font-bold uppercase tracking-wide text-gray-400">
            <div className="w-1.5 shrink-0" />
            {columnas.map((col) => (
              <div key={col.ntcId} className="flex-1 min-w-0 px-2 py-2 border-r border-gray-100 truncate">
                {col.nombre}
              </div>
            ))}
          </div>

          {/* Data rows — grid con rowSpan: filas consecutivas de visibleRows que comparten el
              mismo requisito en una columna se combinan en una sola celda (antes salian
              repetidas en filas separadas). */}
          {(() => {
            const rowSpans = calcularSpansStepper(visibleRows, columnas);
            return (
              <div className="grid" style={{ gridTemplateColumns: `6px ${columnas.map(() => '1fr').join(' ')}` }}>
                {visibleRows.map((ternaDef, i) => (
                  <div
                    key={`diff-${ternaDef.id}`}
                    style={{ gridColumn: 1, gridRow: `${i + 1} / span 1` }}
                    className="flex items-center justify-center py-3 border-b border-gray-50"
                  >
                    {defaultRows.some((r) => r.id === ternaDef.id) && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 block" />}
                  </div>
                ))}
                {columnas.map((col, colIdx) =>
                  (rowSpans[col.orden] ?? []).map((s, i) => {
                    const req = s.reqId ? reqMap.get(s.reqId) : null;
                    const cal = req ? (calMap.get(req.requisitoId) ?? null) : null;
                    const prevCal = req ? prevCalMap.get(req.requisitoId) : undefined;
                    const calChanged = prevCal !== undefined && prevCal !== cal;
                    const evalReqIntId = req ? evalReqIdMap.get(req.requisitoId) : undefined;
                    const estadoRevision = req ? evalEstadoMap.get(req.requisitoId) : undefined;
                    const isReqPendiente = calChanged && (!estadoRevision || estadoRevision === 'PENDIENTE');
                    const tieneEvaluacionRequisito = evalReqIntId != null;
                    const estadoBg = tieneEvaluacionRequisito
                      ? estadoRevision === 'APROBADO'
                        ? 'bg-green-100'
                        : estadoRevision === 'RECHAZADO'
                          ? 'bg-red-100'
                          : 'bg-yellow-100'
                      : '';
                    const entries = evalReqIntId != null ? (historialFieldMap.get(`${evalReqIntId}:REQUISITO`) ?? []) : [];
                    const appealEntry = entries.find((e) => e.estado === 'RESPUESTA_ASESOR');
                    const latestAdminEntry = entries.find(
                      (e) => e.estado === 'APROBADO' || e.estado === 'RECHAZADO' || e.estado === 'COMENTADO'
                    );

                    return (
                      <div
                        key={`cell-${col.ntcId}-${i}`}
                        style={{ gridColumn: colIdx + 2, gridRow: `${s.startRow + 1} / span ${s.span}` }}
                        className={`min-w-0 border-r border-b border-gray-100 last:border-r-0 flex flex-col gap-1 ${estadoBg}`}
                      >
                        {req ? (
                          <ShiftActionOverlay
                            onAprobar={
                              evalReqIntId && onRequisitoVeredicto ? () => onRequisitoVeredicto(evalReqIntId, 'APROBADO') : undefined
                            }
                            onRechazar={
                              evalReqIntId && onRequisitoVeredicto ? () => onRequisitoVeredicto(evalReqIntId, 'RECHAZADO') : undefined
                            }
                            className="h-full"
                          >
                            <div
                              role={evalReqIntId ? 'button' : undefined}
                              tabIndex={evalReqIntId ? 0 : undefined}
                              onClick={
                                evalReqIntId
                                  ? () =>
                                      openRequisitoReview(evalReqIntId, req.numeral, req.capitulo, req.descripcion, cal, prevCal ?? null)
                                  : undefined
                              }
                              onKeyDown={
                                evalReqIntId
                                  ? (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openRequisitoReview(evalReqIntId, req.numeral, req.capitulo, req.descripcion, cal, prevCal ?? null);
                                      }
                                    }
                                  : undefined
                              }
                              className={`flex flex-col h-full min-h-[140px] justify-start gap-1 rounded-md p-2.5 transition-colors ${
                                evalReqIntId ? `cursor-pointer hover:bg-blue-50/60 ${estadoBg}` : estadoBg
                              }`}
                            >
                              <div className="flex items-center gap-1 flex-wrap">
                                {calChanged ? (
                                  <>
                                    <span
                                      className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-full leading-none opacity-50 ${CAL_STYLES[prevCal ?? 'NA'] ?? ''}`}
                                    >
                                      {prevCal ?? '—'}
                                    </span>
                                    <span className="text-gray-300 text-[9px]">→</span>
                                    <span
                                      className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-full leading-none ${CAL_STYLES[cal ?? 'NA'] ?? ''}`}
                                    >
                                      {cal ?? '—'}
                                    </span>
                                  </>
                                ) : cal ? (
                                  <span
                                    className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-full leading-none ${CAL_STYLES[cal]}`}
                                  >
                                    {cal}
                                  </span>
                                ) : null}
                                <span className="text-[10px] font-semibold text-gray-500">{req.numeral}</span>
                                {tieneEvaluacionRequisito && (
                                  <div className="flex items-center gap-1">
                                    {estadoRevision === 'APROBADO' && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border shadow-2xs bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7] inline-flex items-center gap-1">
                                        <Check className="w-2.5 h-2.5 text-[#2d7a4f]" /> Aprobado
                                      </span>
                                    )}
                                    {estadoRevision === 'RECHAZADO' && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border shadow-2xs bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0] inline-flex items-center gap-1">
                                        <XCircle className="w-2.5 h-2.5 text-[#c0392b]" /> Rechazado
                                      </span>
                                    )}
                                    {(!estadoRevision || estadoRevision === 'PENDIENTE') && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold border shadow-2xs bg-[#fef9c3] text-[#854d0e] border-[#fef08a] inline-flex items-center gap-1">
                                        <Clock className="w-2.5 h-2.5 text-[#854d0e]" /> Pendiente
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p
                                className={`text-[10px] text-gray-400 leading-relaxed whitespace-pre-wrap ${expandedDescs ? '' : 'line-clamp-3'}`}
                              >
                                {req.descripcion}
                              </p>
                              <div className="space-y-1 w-full mt-1.5">
                                {appealEntry && (
                                  <div className="p-1.5 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-800 leading-tight">
                                    <span className="font-semibold">
                                      Respuesta del asesor (
                                      {new Date(appealEntry.fecha).toLocaleString('es-CO', {
                                        timeZone: 'UTC',
                                        hour12: true,
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                      ):
                                    </span>{' '}
                                    <span className="italic text-amber-950">"{appealEntry.observacion}"</span>
                                  </div>
                                )}
                                {latestAdminEntry?.observacion && (
                                  <div
                                    className={`p-1.5 rounded border text-[10px] leading-tight ${
                                      latestAdminEntry.estado === 'APROBADO'
                                        ? 'bg-green-50/70 border-green-200/60 text-green-800'
                                        : latestAdminEntry.estado === 'RECHAZADO'
                                          ? 'bg-red-50/70 border-red-200/60 text-red-800'
                                          : 'bg-blue-50/70 border-blue-200/60 text-blue-800'
                                    }`}
                                  >
                                    <span className="font-semibold">
                                      Comentario de revisión (
                                      {latestAdminEntry.estado === 'APROBADO'
                                        ? 'Aprobado'
                                        : latestAdminEntry.estado === 'RECHAZADO'
                                          ? 'Rechazado'
                                          : 'Comentado'}
                                      ):
                                    </span>{' '}
                                    <span className="italic">"{latestAdminEntry.observacion}"</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </ShiftActionOverlay>
                        ) : (
                          <span className="text-gray-300 text-[10px] p-2.5">—</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })()}

          {/* Matrix footer: expand rows + expand descriptions */}
          <div className="flex border-t border-gray-100">
            {canExpand && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded
                  ? 'Colapsar'
                  : `Ver ${allRowsForOrden.length - defaultRows.length} fila${allRowsForOrden.length - defaultRows.length > 1 ? 's' : ''} más`}
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpandedDescs((v) => !v)}
              className={`${canExpand ? 'border-l border-gray-100 px-4' : 'flex-1'} flex items-center justify-center gap-1 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors`}
            >
              {expandedDescs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expandedDescs ? 'Ocultar descripción' : 'Ver descripción'}
            </button>
          </div>
        </div>

        {/* ── Section 2: Antes / Después — una por cada evalTerna fusionada en este paso ── */}
        {step.evalTernas.map((evalTerna, idx) => {
          const diffTerna = step.diffTernas[idx]!;
          if (
            diffTerna.observaciones === undefined &&
            diffTerna.planMejora === undefined &&
            diffTerna.fechaImplementacion === undefined &&
            diffTerna.evidenciaIds === undefined
          ) {
            return null;
          }
          return (
            <Fragment key={evalTerna.id}>
              {(() => {
                const rawObs = diffTerna.observaciones;
                const antesObs: string | null =
                  rawObs !== undefined ? (typeof rawObs === 'string' ? null : (rawObs.anterior ?? null)) : null;
                const despuesObs: string | null =
                  rawObs !== undefined ? (typeof rawObs === 'string' ? rawObs : (rawObs.activa ?? null)) : null;

                const rawPlan = diffTerna.planMejora;
                const antesPlan: string | null =
                  rawPlan !== undefined ? (typeof rawPlan === 'string' ? null : (rawPlan.anterior ?? null)) : null;
                const despuesPlan: string | null =
                  rawPlan !== undefined ? (typeof rawPlan === 'string' ? rawPlan : (rawPlan.activa ?? null)) : null;

                const rawFecha = diffTerna.fechaImplementacion;
                const antesFechaStr: string | null =
                  rawFecha !== undefined
                    ? typeof rawFecha === 'string'
                      ? null
                      : ((rawFecha as { anterior?: string | null }).anterior ?? null)
                    : null;
                const despuesFechaStr: string | null =
                  rawFecha !== undefined
                    ? typeof rawFecha === 'string'
                      ? rawFecha
                      : ((rawFecha as { activa?: string | null }).activa ?? null)
                    : null;

                const rawEvid = diffTerna.evidenciaIds;
                const activeEvidIds: number[] = rawEvid
                  ? Array.isArray(rawEvid)
                    ? rawEvid
                    : ((rawEvid as DiffActivaEvidenciaValue).activa ?? [])
                  : [];
                const prevEvidIds: number[] = rawEvid
                  ? Array.isArray(rawEvid)
                    ? []
                    : ((rawEvid as DiffActivaEvidenciaValue).anterior ?? [])
                  : [];
                const antesEvids = prevEvidIds.map((id) => data.evidencias.find((e) => e.evidenciaId === id)).filter(Boolean);
                const despuesEvids = activeEvidIds.map((id) => data.evidencias.find((e) => e.evidenciaId === id)).filter(Boolean);

                const hasAntes = !!(antesObs || antesPlan || antesFechaStr || antesEvids.length > 0);
                const hasDespues = !!(despuesObs || despuesPlan || despuesFechaStr || despuesEvids.length > 0);
                if (!hasAntes && !hasDespues) return null;

                return (
                  <div>
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50/40">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setExpandAllTexts((v) => !v)}
                          className={`px-2 py-1 rounded-md text-[10px] font-semibold border transition-colors ${
                            expandAllTexts
                              ? 'bg-gray-800 text-white border-gray-800'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-800'
                          }`}
                        >
                          {expandAllTexts ? 'Colapsar textos' : 'Expandir textos'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setWordDiffAll((v) => !v)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border transition-colors ${
                            wordDiffAll
                              ? 'bg-violet-600 text-white border-violet-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300 hover:text-violet-700'
                          }`}
                        >
                          <GitCompare className="w-3 h-3" />
                          {wordDiffAll ? 'Ocultar diff' : 'Ver diff'}
                        </button>
                      </div>
                    </div>
                    {wordDiffAll ? (
                      /* ── Unified diff view ── */
                      <div className="px-3 py-2.5 flex flex-col gap-1.5 border-b border-gray-100">
                        {despuesObs != null && rawObs !== undefined && (
                          <TernaFieldRevision
                            evalTernaId={evalTerna.id}
                            campo="observaciones"
                            label="Observaciones"
                            onVeredicto={onTernaFieldVeredicto}
                            value={despuesObs}
                            prevValue={antesObs}
                            inDiffContext={!!(antesObs && antesObs !== despuesObs)}
                            forceExpand={expandAllTexts}
                            forceWordDiff={true}
                            onOpenReview={() =>
                              openTernaReview(evalTerna.id, 'observaciones', 'OBSERVACIONES', 'Observaciones', despuesObs, antesObs)
                            }
                            latestHistorial={latestHistorialMap.get(historialKey('OBSERVACIONES', evalTerna.id))}
                          />
                        )}
                        {despuesPlan != null && rawPlan !== undefined && (
                          <TernaFieldRevision
                            evalTernaId={evalTerna.id}
                            campo="planMejora"
                            label="Plan de Mejora"
                            onVeredicto={onTernaFieldVeredicto}
                            value={despuesPlan}
                            prevValue={antesPlan}
                            inDiffContext={!!(antesPlan && antesPlan !== despuesPlan)}
                            forceExpand={expandAllTexts}
                            forceWordDiff={true}
                            onOpenReview={() =>
                              openTernaReview(evalTerna.id, 'planMejora', 'PLANMEJORA', 'Plan de Mejora', despuesPlan, antesPlan)
                            }
                            latestHistorial={latestHistorialMap.get(historialKey('PLANMEJORA', evalTerna.id))}
                          />
                        )}
                        {despuesFechaStr != null && rawFecha !== undefined && (
                          <TernaFieldRevision
                            evalTernaId={evalTerna.id}
                            campo="fechaImplementacion"
                            label="Fecha de Implementación"
                            onVeredicto={onTernaFieldVeredicto}
                            value={formatDate(despuesFechaStr)}
                            prevValue={formatDate(antesFechaStr)}
                            inDiffContext={!!(antesFechaStr && antesFechaStr !== despuesFechaStr)}
                            forceExpand={expandAllTexts}
                            forceWordDiff={true}
                            onOpenReview={() =>
                              openTernaReview(
                                evalTerna.id,
                                'fechaImplementacion',
                                'FECHAIMPLEMENTACION',
                                'Fecha de Implementación',
                                despuesFechaStr,
                                antesFechaStr
                              )
                            }
                            latestHistorial={latestHistorialMap.get(historialKey('FECHAIMPLEMENTACION', evalTerna.id))}
                          />
                        )}
                        {(despuesEvids.length > 0 || antesEvids.length > 0) && (
                          <div className="mt-0.5">
                            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1">Evidencias</div>
                            <div className="flex flex-col gap-1">
                              {antesEvids.map(
                                (ev) =>
                                  ev &&
                                  !despuesEvids.find((d) => d?.evidenciaId === ev.evidenciaId) &&
                                  (ev.url ? (
                                    <a
                                      key={ev.evidenciaId}
                                      href={ev.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-red-100 bg-red-50/50 line-through opacity-60 group/evcard"
                                    >
                                      <ExternalLink className="w-3 h-3 text-red-300 shrink-0" />
                                      <span className="text-[10px] text-red-400 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                    </a>
                                  ) : (
                                    <div
                                      key={ev.evidenciaId}
                                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-red-100 bg-red-50/50 line-through opacity-60"
                                    >
                                      <span className="text-[10px] text-red-400 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                    </div>
                                  ))
                              )}
                              {despuesEvids.map(
                                (ev) =>
                                  ev &&
                                  (ev.url ? (
                                    <a
                                      key={ev.evidenciaId}
                                      href={ev.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-blue-50/40 hover:border-blue-200 hover:shadow-sm transition-all group/evcard"
                                    >
                                      <ExternalLink className="w-3 h-3 text-gray-300 group-hover/evcard:text-blue-500 shrink-0 transition-colors" />
                                      <span className="text-[10px] text-gray-700 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                    </a>
                                  ) : (
                                    <div
                                      key={ev.evidenciaId}
                                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 bg-white"
                                    >
                                      <span className="text-[10px] text-gray-600 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                    </div>
                                  ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* ── Two-column layout: Antes / Después ── */
                      <div className="grid grid-cols-2 divide-x divide-gray-100 border-b border-gray-100">
                        {/* Antes */}
                        <div className="px-3 py-2.5 flex flex-col gap-1.5">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">Antes</div>
                          {antesObs && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="observaciones"
                              label="Observaciones"
                              value={antesObs}
                              inDiffContext={false}
                              forceExpand={expandAllTexts}
                            />
                          )}
                          {antesPlan && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="planMejora"
                              label="Plan de Mejora"
                              value={antesPlan}
                              inDiffContext={false}
                              forceExpand={expandAllTexts}
                            />
                          )}
                          {antesFechaStr && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="fechaImplementacion"
                              label="Fecha de Implementación"
                              value={formatDate(antesFechaStr)}
                              inDiffContext={false}
                              forceExpand={expandAllTexts}
                            />
                          )}
                          {antesEvids.length > 0 && (
                            <div className="mt-0.5">
                              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1">Evidencias</div>
                              <div className="flex flex-col gap-1">
                                {antesEvids.map(
                                  (ev) =>
                                    ev &&
                                    (ev.url ? (
                                      <a
                                        key={ev.evidenciaId}
                                        href={ev.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 hover:border-gray-200 transition-all group/evcard"
                                      >
                                        <ExternalLink className="w-3 h-3 text-gray-300 group-hover/evcard:text-gray-400 shrink-0 transition-colors" />
                                        <span className="text-[10px] text-gray-400 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                      </a>
                                    ) : (
                                      <div
                                        key={ev.evidenciaId}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-100 bg-gray-50"
                                      >
                                        <span className="text-[10px] text-gray-400 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                      </div>
                                    ))
                                )}
                              </div>
                            </div>
                          )}
                          {!hasAntes && <span className="text-[10px] text-gray-300 italic">Sin datos previos</span>}
                        </div>

                        {/* Después */}
                        <div className="px-3 py-2.5 flex flex-col gap-1.5">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-blue-500 mb-0.5">Después</div>
                          {despuesObs != null && rawObs !== undefined && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="observaciones"
                              label="Observaciones"
                              onVeredicto={onTernaFieldVeredicto}
                              value={despuesObs}
                              inDiffContext={true}
                              forceExpand={expandAllTexts}
                              onOpenReview={() =>
                                openTernaReview(evalTerna.id, 'observaciones', 'OBSERVACIONES', 'Observaciones', despuesObs, antesObs)
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('OBSERVACIONES', evalTerna.id))}
                            />
                          )}
                          {despuesPlan != null && rawPlan !== undefined && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="planMejora"
                              label="Plan de Mejora"
                              onVeredicto={onTernaFieldVeredicto}
                              value={despuesPlan}
                              inDiffContext={true}
                              forceExpand={expandAllTexts}
                              onOpenReview={() =>
                                openTernaReview(evalTerna.id, 'planMejora', 'PLANMEJORA', 'Plan de Mejora', despuesPlan, antesPlan)
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('PLANMEJORA', evalTerna.id))}
                            />
                          )}
                          {despuesFechaStr != null && rawFecha !== undefined && (
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="fechaImplementacion"
                              label="Fecha de Implementación"
                              onVeredicto={onTernaFieldVeredicto}
                              value={formatDate(despuesFechaStr)}
                              inDiffContext={true}
                              forceExpand={expandAllTexts}
                              onOpenReview={() =>
                                openTernaReview(
                                  evalTerna.id,
                                  'fechaImplementacion',
                                  'FECHAIMPLEMENTACION',
                                  'Fecha de Implementación',
                                  despuesFechaStr,
                                  antesFechaStr
                                )
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('FECHAIMPLEMENTACION', evalTerna.id))}
                            />
                          )}
                          {despuesEvids.length > 0 && (
                            <div className="mt-0.5">
                              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1">Evidencias</div>
                              <div className="flex flex-col gap-1">
                                {despuesEvids.map(
                                  (ev) =>
                                    ev &&
                                    (ev.url ? (
                                      <a
                                        key={ev.evidenciaId}
                                        href={ev.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-blue-50/40 hover:border-blue-200 hover:shadow-sm transition-all group/evcard"
                                      >
                                        <ExternalLink className="w-3 h-3 text-gray-300 group-hover/evcard:text-blue-500 shrink-0 transition-colors" />
                                        <span className="text-[10px] text-gray-700 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                      </a>
                                    ) : (
                                      <div
                                        key={ev.evidenciaId}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 bg-white"
                                      >
                                        <span className="text-[10px] text-gray-600 flex-1 min-w-0 truncate">{ev.nombre}</span>
                                      </div>
                                    ))
                                )}
                              </div>
                            </div>
                          )}
                          {!hasDespues && <span className="text-[10px] text-gray-300 italic">Sin datos nuevos</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </Fragment>
          );
        })}
      </div>

      {/* Footer: step status */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
        <div className="text-xs text-gray-400">
          {isSaved ? (
            <span className="text-green-600 font-medium">Sin pendientes</span>
          ) : (
            <span className="text-amber-500">{totalPendienteActual} pendiente(s)</span>
          )}
        </div>
      </div>

      {reviewModal && (
        <RevisionFieldModal
          open
          onClose={() => setReviewModal(null)}
          titulo={reviewModal.titulo}
          kind={reviewModal.kind}
          textoActual={reviewModal.textoActual}
          textoAnterior={reviewModal.textoAnterior}
          numeral={reviewModal.numeral}
          capitulo={reviewModal.capitulo}
          calActual={reviewModal.calActual}
          calAnterior={reviewModal.calAnterior}
          historialList={reviewModal.historialList}
          estadoActual={reviewModal.estadoActual}
          observacionActual={reviewModal.observacionActual}
          onAprobar={async (observacion) => {
            await reviewModal.onAprobar?.(observacion);
            setReviewModal(null);
          }}
          onRechazar={async (observacion) => {
            await reviewModal.onRechazar?.(observacion);
            setReviewModal(null);
          }}
        />
      )}
    </div>
  );
}
